import { z } from "zod";

/**
 * ENT assessment — otoscopy + audiometry + nasal/throat/neck findings. The
 * pure-tone average (PTA) per ear is derived deterministically; everything
 * else is entered as observed.
 */

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
const dbThreshold = z.coerce
	.number()
	.int()
	.min(-10)
	.max(120)
	.nullable()
	.optional();

export const entAssessmentApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	assessment_date: dateStr,
	ear_right_otoscopy: z.string().nullable(),
	ear_left_otoscopy: z.string().nullable(),
	aud_right_500: z.number().int().nullable(),
	aud_right_1000: z.number().int().nullable(),
	aud_right_2000: z.number().int().nullable(),
	aud_right_4000: z.number().int().nullable(),
	aud_left_500: z.number().int().nullable(),
	aud_left_1000: z.number().int().nullable(),
	aud_left_2000: z.number().int().nullable(),
	aud_left_4000: z.number().int().nullable(),
	pta_right: z.number().int().nullable(),
	pta_left: z.number().int().nullable(),
	nose_findings: z.string().nullable(),
	throat_findings: z.string().nullable(),
	neck_findings: z.string().nullable(),
	assessment: z.string().nullable(),
	plan: z.string().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type EntAssessmentApi = z.infer<typeof entAssessmentApiSchema>;

export const createEntAssessmentInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	assessment_date: dateStr,
	ear_right_otoscopy: z.string().max(1000).nullable().optional(),
	ear_left_otoscopy: z.string().max(1000).nullable().optional(),
	aud_right_500: dbThreshold,
	aud_right_1000: dbThreshold,
	aud_right_2000: dbThreshold,
	aud_right_4000: dbThreshold,
	aud_left_500: dbThreshold,
	aud_left_1000: dbThreshold,
	aud_left_2000: dbThreshold,
	aud_left_4000: dbThreshold,
	nose_findings: z.string().max(1000).nullable().optional(),
	throat_findings: z.string().max(1000).nullable().optional(),
	neck_findings: z.string().max(1000).nullable().optional(),
	assessment: z.string().max(2000).nullable().optional(),
	plan: z.string().max(2000).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateEntAssessmentInput = z.infer<
	typeof createEntAssessmentInputSchema
>;

// ===== Deterministic pure-tone average (PTA) =====

/**
 * Pure-tone average (dB HL) = mean of the available 500/1000/2000/4000 Hz
 * thresholds for one ear. Pure + deterministic. Returns null when no threshold
 * is provided; otherwise averages whatever subset is present (rounded).
 */
export function computePta(
	thresholds: Array<number | null | undefined>,
): number | null {
	const vals = thresholds.filter(
		(v): v is number => v != null && Number.isFinite(v),
	);
	if (vals.length === 0) return null;
	const sum = vals.reduce((a, b) => a + b, 0);
	return Math.round(sum / vals.length);
}

export type HearingGrade =
	| "normal"
	| "mild"
	| "moderate"
	| "moderately_severe"
	| "severe"
	| "profound";

/** WHO hearing-impairment grades from a PTA (dB HL). */
export function hearingGrade(
	pta: number | null | undefined,
): HearingGrade | null {
	if (pta == null || !Number.isFinite(pta)) return null;
	if (pta <= 25) return "normal";
	if (pta <= 40) return "mild";
	if (pta <= 55) return "moderate";
	if (pta <= 70) return "moderately_severe";
	if (pta <= 90) return "severe";
	return "profound";
}
